import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, info: null, error: null });
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/password-resets/${token}`);
        setState({ loading: false, info: data, error: null });
      } catch (e) {
        setState({ loading: false, info: null, error: e.response?.data?.detail || "Link invalid" });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw1 || pw1.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pw1 !== pw2) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/password-resets/${token}`, { password: pw1 });
      setDone(true);
      toast.success("Password updated — please sign in");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="reset-password-page">
      <Card className="rounded-sm border-zinc-200 w-full max-w-md">
        <CardContent className="p-8 space-y-5">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-[#002FA7] text-white rounded-sm flex items-center justify-center">
              <KeyRound className="h-4 w-4" />
            </div>
            <div>
              <div className="label-overline">Iceberg Digital</div>
              <h1 className="font-display font-extrabold text-2xl tracking-tight">Reset your password</h1>
            </div>
          </div>

          {state.loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Validating link…
            </div>
          )}

          {!state.loading && state.error && (
            <div className="space-y-3" data-testid="reset-password-error">
              <p className="text-sm text-red-600">{state.error}</p>
              <Link to="/login" className="text-xs label-overline text-[#002FA7] hover:underline">
                Go to sign-in ↗
              </Link>
            </div>
          )}

          {!state.loading && state.info && done && (
            <div className="space-y-3" data-testid="reset-password-success">
              <p className="text-sm text-emerald-700">
                Password updated for <strong>{state.info.email}</strong>.
              </p>
              <Link to="/login"
                className="inline-block text-xs label-overline text-[#002FA7] hover:underline"
                data-testid="reset-password-go-login">
                Sign in ↗
              </Link>
            </div>
          )}

          {!state.loading && state.info && !done && (
            <>
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs space-y-0.5">
                <div className="text-zinc-500">Resetting password for</div>
                <div className="font-display font-bold text-sm">{state.info.name}</div>
                <div className="font-mono text-zinc-600">{state.info.email}</div>
              </div>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="label-overline">New password (min 8 chars) <span className="text-red-600 ml-0.5">*</span></Label>
                  <Input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
                    className="rounded-sm h-10" data-testid="reset-password-input" required />
                </div>
                <div className="space-y-1.5">
                  <Label className="label-overline">Confirm new password <span className="text-red-600 ml-0.5">*</span></Label>
                  <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                    className="rounded-sm h-10" data-testid="reset-password-confirm" required />
                </div>
                <Button type="submit" disabled={busy}
                  className="w-full rounded-sm bg-zinc-900 hover:bg-black text-white h-10"
                  data-testid="reset-password-submit">
                  {busy ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
